/**
 * PhotoCreditSheet — the full credit for one photo, opened by tapping a
 * compact PhotoCredit (md/sm/xs). Title, author, license (linked to its text)
 * and the photo's page at its source: everything a CC credit asks for
 * ("title, author, source, license"), reachable from a thumbnail through one
 * tap target. docs/WIKIMEDIA_ATTRIBUTION.md rule 4.
 *
 * The shared Modal, so focus trap, Escape and stacking come for free — it
 * opens above another modal (a picker) by stack depth. Not used in the
 * lightbox: that shows the lg credit, whose parts are links already.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';

function Row({ label, children }) {
  return (
    <div className="flex gap-3 py-1.5">
      <dt className="w-20 flex-shrink-0 text-content-secondary">{label}</dt>
      <dd className="min-w-0 break-words text-content-primary">{children}</dd>
    </div>
  );
}

function Link({ href, children }) {
  if (!href) return children;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary-dark">
      {children}
    </a>
  );
}

function PhotoCreditSheet({ credit, onClose }) {
  const { t } = useTranslation();
  const isGoogle = credit.source === 'google';
  const isTiqets = credit.source === 'tiqets';
  const licenseText = credit.license || (credit.public_domain ? t('photoCredit.publicDomain') : null);

  return (
    <Modal isOpen onClose={onClose} size="sm" ariaLabel={t('photoCredit.sheetTitle')}>
      <Modal.Header onClose={onClose}>{t('photoCredit.sheetTitle')}</Modal.Header>
      <Modal.Body>
        {isTiqets ? (
          <p className="text-sm text-content-primary">{t('photoCredit.tiqetsCourtesy')}</p>
        ) : (
          <dl className="text-sm">
            {credit.title && <Row label={t('photoCredit.titleLabel')}>{credit.title}</Row>}
            <Row label={t('photoCredit.authorLabel')}>
              <span className="inline-flex items-center gap-1.5">
                {isGoogle && credit.author_photo_url && (
                  <img src={credit.author_photo_url} alt="" referrerPolicy="no-referrer" className="w-5 h-5 rounded-full" />
                )}
                <Link href={credit.author_url}>{credit.author || t('photoCredit.unknownAuthor')}</Link>
              </span>
            </Row>
            {licenseText && (
              <Row label={t('photoCredit.licenseLabel')}>
                <Link href={credit.license_url}>{licenseText}</Link>
              </Row>
            )}
          </dl>
        )}
        {credit.source_url && (
          <a
            href={credit.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            {isGoogle ? t('photoCredit.viewOnGoogleMaps') : t('photoCredit.viewOnCommons')}
            <span aria-hidden="true">↗</span>
          </a>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default PhotoCreditSheet;
